<template>
  <!-- TKP-159: koşumun birim tablosu — Devam Ediyor'da tuşlu, Tamamlandı görüntülemesinde salt okunur (`readonly`).
       TKP-160 / tunesoft2 s.9: Birim, Sorumlu, Durum (Devam Ediyor / Tamamlandı), Tamamlanma Zamanı; satırda
       "Güncelleme Tamamlandı" ve "Güncelleme Yok" tuşları (işaretlenen vurgulu). TKP-105/135 süreç satırları korunur. -->
  <table class="table table-sm mb-0" data-test="period-table">
    <thead>
      <tr>
        <th>{{ $t("approval.department") }}</th>
        <th>{{ $t("approval.period.assignee") }}</th>
        <th>{{ $t("approval.period.state") }}</th>
        <th>{{ $t("approval.period.closed-at") }}</th>
        <th v-if="!readonly" class="text-right">{{ $t("approval.period.remind") }}</th>
        <th class="text-right"></th>
      </tr>
    </thead>
    <tbody>
      <template v-for="entry in period.departments">
        <tr :key="entry.department" data-test="period-entry" :data-entry-status="entry.status">
          <td>
            {{ entry.name | translate }}
            <!-- TKP-135: satırın periyodu (birimin kendisi ya da varsayılan) -->
            <small v-if="entry.months" class="text-muted ml-1" data-test="entry-months">· {{ $t("approval.period.every", { months: entry.months }) }}</small>
          </td>
          <!-- TKP-181 md.102: birimin bütün yetkilileri -->
          <td data-test="entry-assignees">{{ responsibles(entry) }}</td>
          <td>
            <b-badge :variant="closed(entry) ? 'success' : entry.status === 'SUBMITTED' ? 'warning' : 'light'" data-test="entry-state">{{ $t(stateKey(entry)) }}</b-badge>
            <small v-if="entry.status === 'SUBMITTED'" class="text-muted ml-1">· {{ $t("approval.period.entry.SUBMITTED") }}</small>
            <!-- TKP-179 md.63: süreç dağılımı -->
            <!-- TKP-200 m.4 / TKP-209: yalnız "X süreçte güncelleme yok, Y süreç güncellendi" — bekleyen sayısı özette yok (süreç satırları gösterir) -->
            <small v-if="distribution(entry).total" class="text-muted ml-1" data-test="entry-process-summary">· {{ $t("approval.period.process-summary", distribution(entry)) }}</small>
          </td>
          <td><small data-test="entry-closed-at">{{ entry.closedAt ? $options.filters.date(entry.closedAt, true) : "—" }}</small></td>
          <td v-if="!readonly" class="text-right text-nowrap">
            <small v-if="entry.remindedAt" class="text-muted mr-2" data-test="reminded-at">{{ $t("approval.period.reminded-at") }}: {{ entry.remindedAt | date }}</small>
            <b-button v-if="canRemindEntry(entry)" size="sm" variant="outline-warning" :disabled="busy" data-test="remind" @click="$emit('remind', entry)">{{ $t("approval.period.remind") }}</b-button>
          </td>
          <td class="text-right text-nowrap">
            <!-- işaretleme tuşları: seçilen vurgulu; kapalı satırda ya da yetkisizde salt gösterim.
                 TKP-179 md.63: süreçlerden biri işaretliyse tuşlar yalnız kalan süreçlere uygulanır — etiket bunu söyler; kapalı satırda geri alma -->
            <!-- TKP-200 m.6: koşum sürerken burada envanter değişikliği uygulandıysa "Güncelleme yapıldı"; "Güncelleme Yok" kapalı -->
            <small v-if="changed(entry) && !closed(entry)" class="text-success mr-2" data-test="changed-label"><i class="bx bx-check"></i> {{ $t("approval.period.changed") }}</small>
            <small v-if="canMark(entry) && hasMarked(entry)" class="text-muted mr-1" data-test="remaining-label">{{ $t("approval.period.remaining-processes") }}:</small>
            <b-button v-if="canReopen(entry)" size="sm" variant="outline-warning" class="mr-1" :disabled="busy" data-test="reopen" @click="$emit('reopen', entry)">{{ $t("approval.period.reopen") }}</b-button>
            <b-button v-if="canMark(entry) || entry.status === 'UPDATED'" size="sm" class="mr-1" :variant="entry.status === 'UPDATED' ? 'success' : 'outline-success'" :disabled="busy || !canMark(entry)" :data-test="entry.status === 'UPDATED' ? 'updated-chosen' : 'updated'" @click="$emit('updated', entry)">{{ $t("approval.period.updated") }}</b-button>
            <b-button v-if="canMark(entry) || entry.status === 'NO_CHANGE'" size="sm" :variant="entry.status === 'NO_CHANGE' ? 'secondary' : 'outline-secondary'" :disabled="busy || !canMark(entry) || changed(entry)" :title="changed(entry) ? $t('approval.period.changed-hint') : undefined" :data-test="entry.status === 'NO_CHANGE' ? 'no-change-chosen' : 'no-change'" @click="$emit('no-change', entry)">{{ $t("approval.period.no-change") }}</b-button>
          </td>
        </tr>
        <!-- TKP-135 / matris E "teyit beyanı: Süreç Yöneticisi kendi süreci": birimin süreç satırları -->
        <tr v-for="proc in entry.processes || []" :key="`${entry.department}-${proc.process}`" class="table-light" data-test="period-process" :data-process-id="proc.process">
          <td class="pl-4"><small>↳ {{ proc.name | translate }}</small></td>
          <td><small data-test="process-assignees">{{ responsibles(proc) }}</small></td>
          <!-- TKP-209: kapalı birimde açık kalmış süreç birimin durumuyla (eski koşumlar) -->
          <td><b-badge :variant="['UPDATED', 'NO_CHANGE'].includes(processStatus(entry, proc)) ? 'success' : 'light'" data-test="process-state">{{ $t(`approval.period.entry.${processStatus(entry, proc)}`) }}</b-badge></td>
          <td><small>{{ proc.closedAt ? $options.filters.date(proc.closedAt, true) : "—" }}</small></td>
          <td v-if="!readonly"></td>
          <td class="text-right text-nowrap">
            <!-- TKP-179 md.63: süreçte de "Güncelleme Tamamlandı"; kapalı süreç satırında geri alma -->
            <small v-if="changed(proc) && !closed(proc)" class="text-success mr-2" data-test="process-changed-label"><i class="bx bx-check"></i> {{ $t("approval.period.changed") }}</small>
            <b-button v-if="canProcessReopen(entry, proc)" size="sm" variant="outline-warning" class="mr-1" :disabled="busy" data-test="process-reopen" @click="$emit('process-reopen', proc)">{{ $t("approval.period.reopen") }}</b-button>
            <b-button v-if="canProcessNoChange(entry, proc)" size="sm" variant="outline-success" class="mr-1" :disabled="busy" data-test="process-updated" @click="$emit('process-updated', proc)">{{ $t("approval.period.updated") }}</b-button>
            <b-button v-if="canProcessNoChange(entry, proc)" size="sm" variant="outline-secondary" :disabled="busy || changed(proc)" :title="changed(proc) ? $t('approval.period.changed-hint') : undefined" data-test="process-no-change" @click="$emit('process-no-change', proc)">{{ $t("approval.period.no-change") }}</b-button>
          </td>
        </tr>
      </template>
    </tbody>
  </table>
</template>

<script>
import { canDeclareNoChange, canDeclareProcessNoChange, canRemind, canReopenEntry, canReopenProcess, changedDuringRun, hasMarkedProcesses, periodEntryClosed, periodEntryStateKey, processDistribution, processStatusOf, responsibleNames } from "./approval";

export default {
  name: "period-departments-table",
  props: {
    period: { type: Object, required: true },
    readonly: { type: Boolean, default: false },
    busy: { type: Boolean, default: false },
    approver: { type: Boolean, default: false },
    adminDepartmentIds: { type: Array, default: () => [] },
    adminProcessIds: { type: Array, default: () => [] },
  },
  methods: {
    closed: periodEntryClosed,
    stateKey: periodEntryStateKey,
    distribution: processDistribution, // TKP-179
    processStatus: processStatusOf, // TKP-209
    hasMarked: hasMarkedProcesses, // TKP-179
    changed: changedDuringRun, // TKP-200 m.6
    responsibles: responsibleNames, // TKP-181 md.102
    canReopen(entry) {
      return !this.readonly && canReopenEntry(this.period, entry, { approver: this.approver, adminDepartmentIds: this.adminDepartmentIds });
    },
    canProcessReopen(entry, proc) {
      return !this.readonly && canReopenProcess(this.period, entry, proc, { approver: this.approver, adminDepartmentIds: this.adminDepartmentIds, adminProcessIds: this.adminProcessIds });
    },
    /** "Güncelleme Tamamlandı" / "Güncelleme Yok": aynı kural (dönem açık, satır açık/onayda, onay yetkilisi ya da birimin yetkilisi). */
    canMark(entry) {
      return !this.readonly && canDeclareNoChange(this.period, entry, { approver: this.approver, adminDepartmentIds: this.adminDepartmentIds });
    },
    canProcessNoChange(entry, proc) {
      return !this.readonly && canDeclareProcessNoChange(this.period, entry, proc, { approver: this.approver, adminDepartmentIds: this.adminDepartmentIds, adminProcessIds: this.adminProcessIds });
    },
    canRemindEntry(entry) {
      return !this.readonly && canRemind(this.period, entry, { approver: this.approver, adminDepartmentIds: this.adminDepartmentIds });
    },
  },
};
</script>
